import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { CakeMark } from "../components/Logo";

const ease = [0.22, 1, 0.36, 1];
const TOKEN_KEY = "velvet_chat_token";

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [thread, setThread] = useState(null);
  const [form, setForm] = useState({ name: "", contact: "", message: "" });
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const fetchThread = (t = token) => {
    if (!t) return;
    api.get(`/messages/thread/${t}`).then((res) => setThread(res.data)).catch(() => {
      localStorage.removeItem(TOKEN_KEY);
      setToken("");
    });
  };

  useEffect(() => {
    if (token) fetchThread(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!open || !token) return;
    const id = setInterval(() => fetchThread(token), 8000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, token]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [thread?.messages?.length, open]);

  const start = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await api.post("/messages", form);
      localStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
      toast.success("Message sent — Lall will reply right here.");
    } catch (err) {
      toast.error(formatApiError(err, "Could not send your message. Please try again."));
    } finally {
      setSending(false);
    }
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    try {
      await api.post(`/messages/thread/${token}/reply`, { message: reply.trim() });
      setReply("");
      fetchThread();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 1.6, type: "spring", stiffness: 200, damping: 16 }}
        onClick={() => setOpen((o) => !o)}
        data-testid="chat-toggle-button"
        aria-label="Chat with Lall"
        className="fixed bottom-6 right-6 z-[65] w-14 h-14 rounded-full bg-[#8B5D6C] text-[#F9F6F0] border border-[#C5A059]/70 shadow-xl shadow-[#8B5D6C]/30 flex items-center justify-center hover:bg-[#4A2834] transition-colors duration-300 animate-pulse-ring"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.97 }}
            transition={{ duration: 0.4, ease }}
            className="fixed bottom-24 right-4 sm:right-6 z-[65] w-[calc(100vw-2rem)] max-w-sm bg-[#F9F6F0] border border-[#C5A059]/40 rounded-3xl shadow-2xl shadow-[#4A2834]/30 overflow-hidden flex flex-col"
            data-testid="chat-panel"
          >
            <div className="bg-[#8B5D6C] px-6 py-5 flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-[#F9F6F0]/15 flex items-center justify-center">
                <CakeMark className="w-6 h-6" stroke="#E8D8B6" />
              </div>
              <div>
                <p className="font-serif text-lg text-[#F9F6F0] leading-none">Chat with Lall</p>
                <p className="text-[11px] text-[#F9F6F0]/70 mt-1.5">Usually replies within a few hours</p>
              </div>
            </div>

            {!token || !thread ? (
              <form onSubmit={start} className="p-6 space-y-4" data-testid="chat-start-form">
                <p className="text-sm text-[#70505D] leading-relaxed">
                  A question about a cake, a date, or an idea? Drop me a message here.
                </p>
                <input className="field" required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="chat-name-input" />
                <input className="field" required placeholder="Email or phone (so I can reach you)" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} data-testid="chat-contact-input" />
                <textarea className="field min-h-[90px] resize-none" required placeholder="Your message…" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="chat-message-input" />
                <button type="submit" disabled={sending} data-testid="chat-send-button" className="w-full py-3.5 rounded-full bg-[#8B5D6C] text-[#F9F6F0] text-sm font-medium border border-[#C5A059]/60 hover:bg-[#4A2834] disabled:opacity-60 transition-colors">
                  {sending ? "Sending…" : "Start the conversation"}
                </button>
              </form>
            ) : (
              <>
                <div className="p-5 space-y-3 max-h-72 overflow-y-auto" data-testid="chat-thread">
                  {thread.messages.map((m) => (
                    <div key={m.id} className={`flex ${m.sender === "customer" ? "justify-end" : "justify-start"}`}>
                      <div
                        className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                          m.sender === "customer"
                            ? "bg-[#8B5D6C] text-[#F9F6F0] rounded-br-sm"
                            : "bg-[#F3EEE6] text-[#4A2834] border border-[#C5A059]/30 rounded-bl-sm"
                        }`}
                      >
                        {m.sender === "owner" && <p className="text-[10px] uppercase tracking-[0.2em] text-[#C5A059] mb-1">Lall</p>}
                        {m.text}
                      </div>
                    </div>
                  ))}
                  <div ref={bottomRef} />
                </div>
                <form onSubmit={sendReply} className="p-4 border-t border-[#8B5D6C]/10 flex gap-2">
                  <input className="field flex-1" placeholder="Write a reply…" value={reply} onChange={(e) => setReply(e.target.value)} data-testid="chat-reply-input" />
                  <button type="submit" disabled={sending} data-testid="chat-reply-button" aria-label="Send reply" className="shrink-0 w-11 h-11 rounded-full bg-[#8B5D6C] text-[#F9F6F0] flex items-center justify-center hover:bg-[#4A2834] disabled:opacity-60 transition-colors">
                    <Send size={16} />
                  </button>
                </form>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;
